import React, {
  KeyboardEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import classnames from "classnames";
import { ToolbarProps } from "./toolbar.types";
import useResizeObserver from "../utils/useResizeObserver";
import { toolbarItemsParser } from "./utils/toolbarItemsParser";
import { MeasureHelper } from "./utils/MeasureHelper";
import { initialState, ToolbarContext } from "./utils/toolbarContext";
import { reducer } from "./utils/toolbarReducer";
import { ToolbarActionType } from "./utils/toolbarReducer.types";
import { normalizeToIdFormat } from "../utils";
import { ToolbarItem } from "./components/toolbar-item";
import { ToolbarItemProps } from "./components/toolbar-item.types";
import {
  ToolbarDisabledItemCss,
  ToolbarCustomIconSvgCss,
  ToolbarShowMoreButtonTextCss,
  ToolbarShowItemsInSmallScreenCss,
} from "./css-overrides";
import classNames from "classnames";

export const Toolbar = ({
  textButtons = false,
  className,
  children,
  showMoreButtonText,
  showToolbarItemsInSmallScreen = false,
  ...rest
}: ToolbarProps): ReactElement | null => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [toolbarTabindex, setToolbarTabindex] = useState(0);
  const [animateShowMoreDropdown, setAnimateShowMoreDropdown] = useState(false);
  const [openShowMoreDropdown, setOpenShowMoreDropdown] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);

  const measureInProgress = state.measureInProgress;
  const settingVisibilityInProgress = state.settingVisibilityInProgress;

  const { width: toolbarContainerWidth } = useResizeObserver({
    ref: toolbarRef,
  });
  const [activeToolbarElement, setActiveToolbarElement] = useState<number>();

  const toolbarSchema = useMemo(
    () => toolbarItemsParser(React.Children.toArray(children), textButtons),
    [children, textButtons]
  );

  const toolbarSchemaStringify = JSON.stringify(toolbarSchema);

  /**
   * Local "openShowMoreDropdown" and "animateShowMoreDropdown" states added to handle the delay needed
   * for animation when opening and closing the dropdown
   */
  const toggleShowMoreDropdown = (open: boolean): void => {
    if (open === true) {
      setOpenShowMoreDropdown(true);
      window.setTimeout(() => setAnimateShowMoreDropdown(true), 0);
    } else {
      setAnimateShowMoreDropdown(false);
      timeoutRef.current = window.setTimeout(
        () => setOpenShowMoreDropdown(false),
        100
      );
    }
  };

  // Closing ShowMore dropdown from ToolbarItem level, if needed
  useEffect(() => {
    const isFloatingUIPortalElement = document.querySelector(
      "[data-floating-ui-portal]"
    );
    // don't close dropdowns if floating UI portal is open (opened from within ToolbarItem)
    if (!isFloatingUIPortalElement) {
      if (state.closeShowMoreDropdown) {
        toggleShowMoreDropdown(false);
        dispatch({
          type: ToolbarActionType.CLOSE_SHOW_MORE_DROPDOWN,
          inProgress: false,
        });
      }
    }
  }, [dispatch, state.closeShowMoreDropdown]);

  // Clearing timeout on unmount. Needs a separate useEffect with an empty deps array.
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const closeAllToolbarItemDropdowns = (): void => {
    dispatch({
      type: ToolbarActionType.CLOSE_ALL_TOOLBAR_ITEM_DROPDOWNS,
      inProgress: true,
    });
  };

  const checkIsSmallScreen = (): void => {
    const { width: windowWidth } = document.body.getBoundingClientRect();
    dispatch({
      type: ToolbarActionType.SET_IS_SMALL_SCREEN,
      windowWidth,
    });
  };

  const hiddenContainer = toolbarRef.current?.querySelector(
    ".spark-toolbar__container--hidden"
  );
  const showMoreButton = toolbarRef.current?.querySelector(
    ".spark-toolbar__show-more"
  );

  const _onWindowResize = useCallback((): void => {
    checkIsSmallScreen();
  }, []);

  useEffect(() => {
    dispatch({
      type: ToolbarActionType.CLEAR_MEASUREMENT,
    });
    dispatch({
      type: ToolbarActionType.REPLACE_STATE,
      changedToolbarSchema: toolbarSchema,
    });
    /**
     * Stringify version of toolbarSchema is needed for comparision in
     * dependency array. Otherwise useEffect will execute on every re-render
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolbarSchemaStringify]);

  const _onWindowClick = useCallback(
    (e: MouseEvent): void => {
      if (animateShowMoreDropdown) {
        const isFloatingUIPortalElement = document.querySelector(
          "[data-floating-ui-portal]"
        );
        // don't close dropdowns if floating UI portal is open (opened from within ToolbarItem)
        if (!isFloatingUIPortalElement) {
          if (
            hiddenContainer &&
            !hiddenContainer.contains(e.target as HTMLElement)
          ) {
            toggleShowMoreDropdown(false);
          }
        }
      }
    },
    [hiddenContainer, animateShowMoreDropdown]
  );

  const _onWindowKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key === "Tab") {
        const toolbarEl = toolbarRef.current;
        if (toolbarEl === document.activeElement) {
          const availableElements: NodeListOf<HTMLElement> | undefined =
            toolbarEl?.querySelectorAll(
              ".spark-toolbar__item:not([aria-disabled=true])"
            );
          // give toolbar element a tabindex so the user can refocus the menu using shift-tab;
          setToolbarTabindex(-1);

          if (availableElements) {
            if (activeToolbarElement === undefined) {
              if (hiddenContainer?.contains(availableElements?.[0])) {
                toggleShowMoreDropdown(true);
              }
              availableElements?.[0].focus();
              setActiveToolbarElement(0);
            } else {
              if (
                hiddenContainer?.contains(
                  availableElements?.[activeToolbarElement]
                )
              ) {
                toggleShowMoreDropdown(true);
              }
              availableElements?.[activeToolbarElement ?? 0].focus();
            }
          }
        } else {
          // Restore focusable tabindex for toolbar element
          setToolbarTabindex(0);
        }
      }
    },
    [activeToolbarElement, hiddenContainer]
  );

  const _onWindowFocus = useCallback((e: FocusEvent): void => {
    const isFloatingUIPortalElement = document.querySelector(
      "[data-floating-ui-portal]"
    );
    // don't close dropdowns if floating UI portal is open (opened from within ToolbarItem)
    if (!isFloatingUIPortalElement) {
      if (!toolbarRef.current?.contains(e.target as HTMLElement)) {
        closeAllToolbarItemDropdowns();
        toggleShowMoreDropdown(false);

        setToolbarTabindex(0);
      } else {
        // Set tabindex to -1 so the user can shift-tab out of element
        setToolbarTabindex(-1);
      }
    }
  }, []);

  const _onWindowBlur = useCallback((e: FocusEvent): void => {
    if (toolbarRef.current?.contains(e.target as HTMLElement)) {
      setToolbarTabindex(0);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keyup", _onWindowKeyDown);
    window.addEventListener("click", _onWindowClick);
    window.addEventListener("resize", _onWindowResize);
    /**
     * "focusin" used instead of "focus", otherwise in iframe firefox
     * targets HTMLDocument on first click while focusin bubbles and
     * targets the correct element
     */
    document.addEventListener("focusin", _onWindowFocus, true);
    document.addEventListener("blur", _onWindowBlur, true);
    return () => {
      window.removeEventListener("keyup", _onWindowKeyDown);
      window.removeEventListener("click", _onWindowClick);
      window.removeEventListener("resize", _onWindowResize);
      document.removeEventListener("focusin", _onWindowFocus, true);
      document.removeEventListener("blur", _onWindowBlur, true);
    };
  }, [
    _onWindowKeyDown,
    _onWindowClick,
    _onWindowResize,
    _onWindowFocus,
    _onWindowBlur,
  ]);

  useEffect(() => {
    checkIsSmallScreen();
    if (toolbarContainerWidth && toolbarSchema.length) {
      dispatch({
        type: ToolbarActionType.SET_ITEMS_VISIBILITY,
        toolbarContainerWidth,
        showMoreButtonWidth: showMoreButton?.clientWidth,
        showToolbarItemsInSmallScreen,
      });
    }
  }, [
    showMoreButton?.clientWidth,
    showToolbarItemsInSmallScreen,
    toolbarContainerWidth,
    measureInProgress,
    toolbarSchema.length,
    state.isSmallScreen,
  ]);

  const hasHiddenItems = !!state.toolbarSchema?.find(
    (item) => !item._privateProps?.visible
  );

  const onToolbarKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    const toolbarEl = toolbarRef.current;
    const availableElements: NodeListOf<HTMLElement> | undefined =
      toolbarEl?.querySelectorAll(
        ".spark-toolbar__item:not([aria-disabled=true])"
      );

    // Check if there is already active element, e.g. set with mouse click event
    const clickedActiveElement = availableElements
      ? Array.from(availableElements).findIndex(
          (availableElement) => availableElement === document.activeElement
        )
      : undefined;

    // Filter negative results (when no activeElement index found)
    const filteredClickedActiveElement =
      clickedActiveElement !== undefined && clickedActiveElement >= 0
        ? clickedActiveElement
        : undefined;

    // Set clicked or remembered `activeToolbarElement` as active element
    const activeElement =
      filteredClickedActiveElement ?? activeToolbarElement ?? 0;

    // Update state with `activeToolbarElement` with potential clicked element
    setActiveToolbarElement(activeElement);

    if (
      availableElements &&
      // Making sure we are focusing toolbar item, to prevent arrow actions on children's dropdown
      availableElements[activeElement] === document.activeElement
    ) {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        if (activeElement < (availableElements.length ?? 0) - 1) {
          if (
            hiddenContainer?.contains(
              availableElements[activeElement + 1] as HTMLElement
            )
          ) {
            toggleShowMoreDropdown(true);
          }
          closeAllToolbarItemDropdowns();
          setActiveToolbarElement(activeElement + 1);
          // add timeout needed for dropdown to open
          setTimeout(() => availableElements[activeElement + 1]?.focus(), 0);
        }
      }

      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        if (activeElement > 0) {
          if (
            !hiddenContainer?.contains(
              availableElements[activeElement - 1] as HTMLElement
            )
          ) {
            toggleShowMoreDropdown(false);
          }
          closeAllToolbarItemDropdowns();
          availableElements[activeElement - 1]?.focus();
          setActiveToolbarElement(activeElement - 1);
        }
      }
    }

    if (e.key === "Enter") {
      if (!openShowMoreDropdown && showMoreButton === document.activeElement) {
        toggleShowMoreDropdown(true);
      }
    }
  };

  const renderToolbarItem = (item: ToolbarItemProps): ReactElement => {
    const findToolbarItem = (schemaId: string): ReactElement | null => {
      const toolbarItemBySchemaId = React.Children.toArray(children).find(
        (child) => {
          return (
            ((child as ReactElement).props.id ||
              normalizeToIdFormat((child as ReactElement).props.label)) ===
            schemaId
          );
        }
      );

      return (toolbarItemBySchemaId as ReactElement) ?? null;
    };

    const itemNode = findToolbarItem(item.id as string);

    return (
      <ToolbarItem key={item.id} {...itemNode?.props} {...item}>
        {itemNode?.props.children}
      </ToolbarItem>
    );
  };

  return (
    <ToolbarContext.Provider value={{ state, dispatch }}>
      <div
        className={classnames(
          "spark-toolbar",
          { "spark-toolbar--icon": !textButtons },
          { "show-more": hasHiddenItems },
          { ready: !settingVisibilityInProgress },
          { measured: !measureInProgress },
          { open: openShowMoreDropdown },
          { animate: animateShowMoreDropdown },
          className
        )}
        role="menubar"
        tabIndex={toolbarTabindex}
        ref={toolbarRef}
        onKeyDown={onToolbarKeyDown}
        {...rest}
        style={{ ...rest.style }}
      >
        <div
          className={classNames("spark-toolbar__container--visible", {
            "spark-toolbar__container--visible--auto":
              showToolbarItemsInSmallScreen &&
              state.isSmallScreen &&
              !measureInProgress,
          })}
        >
          {measureInProgress ? (
            <MeasureHelper renderToolbarItem={renderToolbarItem} />
          ) : (
            state.toolbarSchema?.map((item) =>
              item._privateProps?.visible ? renderToolbarItem(item) : null
            )
          )}
        </div>
        <div
          className={classnames("spark-toolbar__show-more", {
            open: openShowMoreDropdown,
            "spark-toolbar__show-more-text": showMoreButtonText,
          })}
          tabIndex={-1}
          onClick={(): void => {
            if (!openShowMoreDropdown) {
              toggleShowMoreDropdown(true);
            }
          }}
        >
          {showMoreButtonText ? (
            <span>{showMoreButtonText}</span>
          ) : (
            <i className="spark-icon-menu-ellipsis-vertical"></i>
          )}
        </div>
        <div className="spark-toolbar__container--hidden">
          {state.toolbarSchema?.map((item) =>
            !item._privateProps?.visible ? renderToolbarItem(item) : null
          )}
        </div>
      </div>
      <ToolbarDisabledItemCss />
      <ToolbarCustomIconSvgCss />
      <ToolbarShowMoreButtonTextCss />
      {showToolbarItemsInSmallScreen &&
        state.isSmallScreen &&
        !measureInProgress && <ToolbarShowItemsInSmallScreenCss />}
    </ToolbarContext.Provider>
  );
};

Toolbar.displayName = "Toolbar";
